import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router-dom'

import { exitImpersonation } from 'ImpersonateHint'
import { Divider, Dropdown, Menu } from 'antd'

import { Feature } from 'core'
import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import { UserRoles } from 'core/application/account/userRoles'
import { ProductIds } from 'core/application/commons/config'
import localStorage from 'core/application/commons/localStorage'

import { useCurrentProduct, useFeatureFlag, useSharedState } from 'common/hooks'

import { Avatar } from '..'
import { ROUTE_PATHS } from '../../routePaths'
import Icon from '../general/Icon'

import './HeaderStyles.less'

interface HeaderUserSectionProps {
  userInfo: UserSectionInfo | null
  user: User
  showProfile?: boolean
  windowWidth?: number
}

export interface UserSectionInfo {
  firstName: string
  lastName: string
}

const HeaderUserSection = ({ userInfo, user, showProfile = true, windowWidth }: HeaderUserSectionProps) => {
  const history = useHistory()
  const { t: translate } = useTranslation()
  const location = useLocation()
  const product = useCurrentProduct()
  const [passwordPath, setPasswordPath] = useState<string>()
  const canShowCoveredDependentsFlag = useFeatureFlag(Feature.ShowCoveredDependents)
  const [, setHideExpensesSmsUpdates] = useSharedState<boolean>('HideExpensesSmsUpdates', false, true)

  let whiteListProducts =
    product.productId === ProductIds.GroupHraId ||
    product.productId === ProductIds.Perks ||
    product.productId === ProductIds.QsehraId ||
    product.productId === ProductIds.IchraId
  const showMyProfile =
    whiteListProducts &&
    user.roles.includes(UserRoles.ProductUser) &&
    user.permissions.includes(Permissions.AccessEligibleEmployeeDashboard)
  const showCoveredDependents =
    product.productId === ProductIds.IchraId &&
    user.roles.includes(UserRoles.ProductUser) &&
    user.permissions.includes(Permissions.AccessEligibleEmployeeDashboard)
  const showTaxSavings =
    product.productId === ProductIds.QsehraId &&
    user.roles.includes(UserRoles.ProductUser) &&
    user.permissions.includes(Permissions.AccessEligibleEmployeeDashboard)

  const showHSAStatus =
    product.productId !== ProductIds.Perks &&
    user.roles.includes(UserRoles.ProductUser) &&
    user.permissions.includes(Permissions.AccessEligibleEmployeeDashboard)

  useEffect(() => {
    const pieces = location.pathname.split('/')
    const pathname = pieces[1]
    setPasswordPath(`/${pathname}/change-password`)
  }, [location])

  const logout = () => {
    if (localStorage.getImpersonatorToken()) {
      exitImpersonation()
    } else {
      localStorage.setToken('')
      localStorage.setImpersonatorToken('')
      localStorage.setSearchedCompanyId('')
      localStorage.setSearchedCompanyName('')
      setHideExpensesSmsUpdates(false)
      localStorage.setDismissedRenewalNotification(false)
      history.push(ROUTE_PATHS.LOGIN_PATH + `?productId=${product.productId}`)
    }
  }

  const userDropdownMenu = (
    <Menu>
      {showProfile && (
        <Menu.Item
          key="0"
          onClick={() => history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_PROFILE)}
          disabled={!showMyProfile}
        >
          {showMyProfile && (
            <>
              <Icon type="far fa-user" fontAwesome className="mr-8" />
              <span>{translate('user-header.my-profile')}</span>
            </>
          )}
        </Menu.Item>
      )}
      <Menu.Item key="1" onClick={() => history.push(passwordPath)}>
        <Icon type="far fa-key" fontAwesome className="mr-8" />
        <span>{translate('user-header.change-password')}</span>
      </Menu.Item>

      {showCoveredDependents && canShowCoveredDependentsFlag && (
        <>
          <Divider className="m-0" />
          <Menu.Item key="2" onClick={() => history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_COVERED_DEPENDENTS)}>
            <span>Covered dependents</span>
          </Menu.Item>
          <Divider className="m-0" />
        </>
      )}

      {showTaxSavings && (
        <>
          <Divider className="m-0" />
          <Menu.Item key="3" onClick={() => history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_TAX_SAVINGS)}>
            <span>{translate('user-header.tax-savings')}</span>
          </Menu.Item>
        </>
      )}

      {showHSAStatus && (
        <>
          <Menu.Item key="4" onClick={() => history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_HSA_STATUS)}>
            <span>HSA status</span>
          </Menu.Item>
          <Divider className="m-0" />
        </>
      )}

      <Menu.Item key="5" onClick={() => logout()}>
        <Icon type="far fa-sign-out" fontAwesome className="mr-8 color-red-6" />
        <span className="color-red-6">{translate('user-header.log-out')}</span>
      </Menu.Item>
    </Menu>
  )

  return userInfo ? (
    <Dropdown overlay={userDropdownMenu} trigger={['hover']} placement="bottomLeft">
      <button type="button" className="ant-dropdown-link grey-link personal-dropdown">
        <Avatar
          shape="circle"
          size="default"
          text={userInfo.firstName[0] + userInfo.lastName[0]}
          className="pk--header-user-icon"
        />
        <span className="ml-8 personal-dropdown--username">
          {userInfo.firstName[0]}. {userInfo.lastName}
          <Icon type="far fa-angle-down" fontAwesome className="ml-8" />
        </span>
      </button>
    </Dropdown>
  ) : null
}

export default HeaderUserSection
